/**
 * The dashboard's derived numbers (PRD 10.1; Phase 22), shared by the browser client and the
 * tests so the KPI strip, the risk lists and the pipeline impact panel are read off the summary
 * (`GET /forecast/summary`) by one set of rules that the server-side tests can hold to the
 * endpoint.
 *
 * Nothing here computes a forecast figure. Every value is a field of the summary, or a sum or
 * ordering of its fields that the Development Plan asks the gate to check ("KPI values equal
 * the summary endpoint; risk lists ordered by hours over"). The one classification made here is
 * the band of the window's average allocation, which the summary leaves to the reader; it uses
 * the run's own thresholds and the engine's rule, pinned by a unit test.
 *
 * Pure: no DOM, no fetch, no formatting — the component formats.
 */
import { col } from './table.js';

/** The shared filters the summary understands, in the API's names. */
export const DASHBOARD_PARAM_KEYS = Object.freeze(['run_id', 'from', 'weeks', 'pipeline']);

/** The KPI tiles, left to right as PRD 10.1 lists them. */
export const KPI_KEYS = Object.freeze(['average', 'peak', 'overload', 'bench']);

/** The two pipeline modes the impact panel always shows, whatever the ribbon is drawing. */
export const IMPACT_MODES = Object.freeze(['weighted', 'unweighted']);

const num = (v) =>
  v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v);

/**
 * The threshold band of an allocation ratio (PRD 7.4), the engine's rule
 * (`utilizationBand` in modules/forecast/core/engine.js) restated for the client, which cannot
 * import the engine. A unit test holds the two together.
 * @param {number | null} utilization
 * @param {{ underload_below: number, caution_above: number, overloaded_above: number }} thresholds
 */
export function bandFor(utilization, thresholds) {
  const u = num(utilization);
  if (u === null || !thresholds) return null;
  if (u < thresholds.underload_below) return 'underload';
  if (u <= thresholds.caution_above) return 'healthy';
  if (u <= thresholds.overloaded_above) return 'caution';
  return 'overloaded';
}

/**
 * The four KPI tiles from a summary. Every number is a summary field; the `average` band is the
 * one derived value (see `bandFor`).
 * @param {any} summary  a `GET /forecast/summary` response
 */
export function dashboardKpis(summary) {
  const a = summary.allocation ?? {};
  const s = summary.signals ?? {};
  const b = summary.bridge ?? {};
  const bw = a.band_weeks ?? { underload: 0, healthy: 0, caution: 0, overloaded: 0 };
  const peak = a.peak ?? null;
  return {
    average: {
      key: 'average',
      utilization: num(a.average_utilization),
      band: bandFor(a.average_utilization, summary.thresholds),
      weeks_count: summary.weeks_count ?? (summary.weeks ?? []).length,
      from: summary.from ?? null,
      to: summary.to ?? null,
      demand_hours: num(b.demand_hours) ?? 0,
      capacity_hours: num(b.capacity_hours) ?? 0,
      headroom_hours: num(b.headroom_hours) ?? 0,
      band_weeks: { ...bw },
      weeks_without_capacity: a.weeks_without_capacity ?? 0,
    },
    peak: peak
      ? {
          key: 'peak',
          utilization: num(peak.utilization),
          band: peak.band ?? null,
          week_start: peak.week_start,
          demand_hours: num(peak.demand_hours) ?? 0,
          capacity_hours: num(peak.capacity_hours) ?? 0,
          headroom_hours: num(peak.headroom_hours) ?? 0,
          over_hours: num(peak.over_hours) ?? 0,
          weeks_over: a.weeks_over ?? 0,
        }
      : { key: 'peak', utilization: null, band: null, week_start: null, weeks_over: 0 },
    overload: {
      key: 'overload',
      services: s.over_counts?.service ?? 0,
      people: s.over_counts?.individual ?? 0,
      departments: s.over_counts?.department ?? 0,
      offices: s.over_counts?.office ?? 0,
      windows: s.windows_over ?? 0,
      deadlines_past: s.deadlines_past ?? 0,
      weeks: (s.weeks?.caution ?? 0) + (s.weeks?.overloaded ?? 0),
      pipeline_mode: s.pipeline_mode ?? null,
      matches_request: s.matches_request ?? true,
    },
    bench: {
      key: 'bench',
      services: s.underload_counts?.service ?? 0,
      people: s.underload_counts?.individual ?? 0,
      departments: s.underload_counts?.department ?? 0,
      offices: s.underload_counts?.office ?? 0,
      windows: s.windows_under ?? 0,
      weeks: a.weeks_under ?? 0,
      hours: num(b.under_hours) ?? 0,
    },
  };
}

/** Whether `rows` are already in descending order of `field` (ties in any order). */
export function orderedByHours(rows, field) {
  for (let i = 1; i < rows.length; i += 1) {
    if (Number(rows[i][field]) > Number(rows[i - 1][field])) return false;
  }
  return true;
}

const byHoursThen = (field, tieField) => (a, b) =>
  Number(b[field]) - Number(a[field]) ||
  String(a[tieField] ?? '').localeCompare(String(b[tieField] ?? ''));

/**
 * The top risk services, most hours over first (the summary's own order, `ORDER BY hours_over
 * DESC, label`). Each row gets its rank and its share of the top row's hours for a proportional
 * bar. `api_ordered` says whether the API already had them in that order; the component reports
 * a false, because a list that is not by hours over is a bug, not a preference.
 * @param {any} summary
 */
export function riskServices(summary) {
  const raw = summary.top_risk?.services ?? [];
  const rows = [...raw].sort(byHoursThen('hours_over', 'label'));
  const top = rows.length ? Number(rows[0].hours_over) : 0;
  return {
    api_ordered: orderedByHours(raw, 'hours_over'),
    rows: rows.map((r, i) => ({
      ...r,
      rank: i + 1,
      hours_over: Number(r.hours_over),
      peak_utilization: num(r.peak_utilization),
      peak_capacity_hours: num(r.peak_capacity_hours) ?? 0,
      /** How far over the peak week ran, as the sketch writes it ("+31%"). */
      peak_over_ratio:
        num(r.peak_utilization) === null
          ? null
          : Math.round((num(r.peak_utilization) - 1) * 10000) / 10000,
      share_of_top: top > 0 ? Number(r.hours_over) / top : 0,
    })),
  };
}

/** `26-ABC-1 Name`, or whichever half exists. */
export function projectLabel(p) {
  return [p.source_project_number, p.name].filter(Boolean).join(' ') || p.project_id || '—';
}

/**
 * The top risk projects, most hours in overloaded service-weeks first (the summary's order,
 * `ORDER BY hours DESC, name`), with rank, label and share of the top row.
 * @param {any} summary
 */
export function riskProjects(summary) {
  const raw = summary.top_risk?.projects ?? [];
  const rows = [...raw].sort(byHoursThen('hours_in_overloaded_weeks', 'name'));
  const top = rows.length ? Number(rows[0].hours_in_overloaded_weeks) : 0;
  return {
    api_ordered: orderedByHours(raw, 'hours_in_overloaded_weeks'),
    rows: rows.map((r, i) => ({
      ...r,
      rank: i + 1,
      label: projectLabel(r),
      hours_in_overloaded_weeks: Number(r.hours_in_overloaded_weeks),
      share_of_top: top > 0 ? Number(r.hours_in_overloaded_weeks) / top : 0,
      flags: [
        r.slipped ? 'slipped' : null,
        r.burn_risk ? 'burn_risk' : null,
        r.entity_type === 'recurring' ? 'recurring' : null,
      ].filter(Boolean),
    })),
  };
}

/**
 * The pipeline's share of the window's capacity in both modes (PRD 10.1: "+18% weighted / +41%
 * unweighted"), with the mode the charts are drawing marked. Shares are the summary's own
 * (`pipeline hours / capacity hours`, null with no capacity).
 * @param {any} summary
 */
export function pipelineImpact(summary) {
  const p = summary.pipeline_impact ?? {};
  const mode = summary.pipeline_mode ?? null;
  return {
    opportunities: p.opportunities ?? 0,
    capacity_hours: num(p.capacity_hours) ?? 0,
    in_use_mode: mode,
    in_use_hours: num(p.in_use_hours) ?? 0,
    rows: IMPACT_MODES.map((m) => ({
      mode: m,
      hours: num(p[`${m}_hours`]) ?? 0,
      share: num(p[`${m}_share`]),
      in_use: mode === m,
    })),
  };
}

/**
 * The KPI strip as table rows, one per figure, so the strip exports like every other view
 * (PRD 7.3: the table is what exports). Values stay numeric; `unit` says how to read them.
 * @param {any} summary
 */
export function kpiRows(summary) {
  const k = dashboardKpis(summary);
  const bw = k.average.band_weeks;
  return [
    {
      key: 'average_utilization',
      metric: 'Average allocation',
      value: k.average.utilization,
      unit: 'ratio',
      detail: k.average.band ?? '',
    },
    {
      key: 'demand_hours',
      metric: 'Demand',
      value: k.average.demand_hours,
      unit: 'hours',
      detail: `${k.average.weeks_count} weeks`,
    },
    {
      key: 'capacity_hours',
      metric: 'Capacity',
      value: k.average.capacity_hours,
      unit: 'hours',
      detail: `${k.average.weeks_count} weeks`,
    },
    {
      key: 'headroom_hours',
      metric: 'Headroom',
      value: k.average.headroom_hours,
      unit: 'hours',
      detail: k.average.headroom_hours < 0 ? 'over capacity' : '',
    },
    {
      key: 'peak_utilization',
      metric: 'Peak allocation',
      value: k.peak.utilization,
      unit: 'ratio',
      detail: k.peak.week_start ? `week of ${k.peak.week_start}` : '',
    },
    {
      key: 'peak_over_hours',
      metric: 'Peak hours over',
      value: k.peak.over_hours ?? 0,
      unit: 'hours',
      detail: k.peak.band ?? '',
    },
    {
      key: 'weeks_over',
      metric: 'Weeks over capacity',
      value: k.peak.weeks_over,
      unit: 'count',
      detail: `of ${k.average.weeks_count}`,
    },
    {
      key: 'band_weeks',
      metric: 'Weeks by state',
      value: k.average.weeks_count,
      unit: 'count',
      detail: `${bw.underload} under, ${bw.healthy} healthy, ${bw.caution} caution, ${bw.overloaded} overloaded`,
    },
    {
      key: 'overload_services',
      metric: 'Services over the line',
      value: k.overload.services,
      unit: 'count',
      detail: `${k.overload.windows} windows, ${k.overload.deadlines_past} deadlines past`,
    },
    {
      key: 'overload_people',
      metric: 'People over the line',
      value: k.overload.people,
      unit: 'count',
      detail: '',
    },
    {
      key: 'overload_departments',
      metric: 'Departments over the line',
      value: k.overload.departments,
      unit: 'count',
      detail: '',
    },
    {
      key: 'bench_services',
      metric: 'Services under-utilized',
      value: k.bench.services,
      unit: 'count',
      detail: `${k.bench.windows} windows`,
    },
    {
      key: 'bench_people',
      metric: 'People under-utilized',
      value: k.bench.people,
      unit: 'count',
      detail: '',
    },
    {
      key: 'bench_departments',
      metric: 'Departments under-utilized',
      value: k.bench.departments,
      unit: 'count',
      detail: '',
    },
    {
      key: 'bench_hours',
      metric: 'Unallocated hours in under-utilized weeks',
      value: k.bench.hours,
      unit: 'hours',
      detail: `${k.bench.weeks} weeks`,
    },
  ];
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// Specifications, not renderings: the screen decorates them with `withFormats` and the CSV
// writer reads the same list.

/** The KPI strip as a table: one row per figure, with the unit that says how to read it. */
export function kpiColumns() {
  return [
    col.text('metric', 'Figure'),
    col.number('value', 'Value'),
    col.text('unit', 'Unit'),
    col.text('detail', 'Detail'),
  ];
}

/** The services over the line, worst first. */
export function riskServiceColumns() {
  return [
    col.count('rank', '#'),
    col.text('label', 'Service'),
    col.hours('hours_over', 'Hours over'),
    col.percent('peak_utilization', 'Peak allocation'),
    col.hours('peak_capacity_hours', 'Capacity at peak'),
    col.week('first_week', 'From'),
    col.count('weeks_over', 'Weeks over'),
    col.text('severity', 'State', { csv: (v) => v ?? '' }),
  ];
}

/** The projects behind those services. */
export function riskProjectColumns() {
  return [
    col.count('rank', '#'),
    col.text('label', 'Project'),
    col.text('client', 'Client'),
    col.text('canonical_type', 'Type'),
    col.hours('hours_in_overloaded_weeks', 'Hours in overloaded weeks'),
    col.count('services', 'Services'),
    col.count('weeks', 'Weeks'),
    col.text('confidence_tier', 'Confidence'),
    col.text('source_mode', 'Source'),
    col.list('flags', 'Flags'),
  ];
}

/** What the pipeline adds, in both modes, and which one the charts are drawn in. */
export function pipelineImpactColumns() {
  return [
    col.text('mode', 'Mode'),
    col.hours('hours', 'Pipeline hours'),
    col.percent('share', 'Share of capacity'),
    col.flag('in_use', 'On the charts'),
  ];
}
